/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Logger } from '@app/core';
import { Venue } from '@app/settings/venues/venue.model';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { VenuesService } from '@app/settings/venues/venues.service';
import { ApiQuery } from '@app/core/http/api-query';
import { CitiesSelectComponent } from '@app/shared/components/cities-select/cities-select.component';
import { NgxGoogleAddressAutocompleteDirective } from 'ngx-google-address-autocomplete';
import { Observable } from 'rxjs';
import { Model } from '@app/shared/models/model.model';
import { CitiesService } from '@app/settings/cities/cities.service';
import { City } from '@app/settings/cities/cities.model';
const log = new Logger('VenuesFormComponent');

@Component({
  selector: 'app-venues-form',
  templateUrl: './venues-form.component.html',
  styleUrls: ['./venues-form.component.scss'],
})
export class VenuesFormComponent implements OnInit {
  @Input() venue: Venue = null;
  fillParentFormCoordinates = true;
  form: FormGroup;
  lastGPCoordinates: object = {
    lat: null,
    long: null,
  };
  GPOptions = {
    types: new Array(),
    fields: ['address_components', 'place_id', 'name', 'geometry'],
  };
  @ViewChild('citySelect', { static: true }) citySelect: CitiesSelectComponent;
  @ViewChild('address', { static: true }) address: NgxGoogleAddressAutocompleteDirective;

  constructor(public modal: NgbActiveModal, private formBuilder: FormBuilder, private venueService: VenuesService, private cityService: CitiesService) {}

  ngOnInit() {
    this.createForm();
    this.generateGPOptions();
    this.patchForm();
  }

  public handleAddressChange(address: any) {
    this.form.get('address')?.setValue(address.name);
    this.form.get('lat')?.setValue(Number(address.lat));
    this.form.get('long')?.setValue(Number(address.lng));

    if (address.address_components && Array.isArray(address.address_components)) {
      let selectedCity = '';
      let selectedCityNames = '';
      if (this.citySelect.selectedCity === null){
        selectedCity = this.form.get('city').value.name;
      } else {
        selectedCity = this.citySelect.selectedCity.name; // The selected city from the city picker
        selectedCityNames = this.citySelect.selectedCity.alternatenames ?? "";
      }
      const addressComponents = address.address_components;
      let foundCity = '';

      for (const component of addressComponents) {
        if (component.types.includes('locality')) {
          foundCity = component.long_name;
          break;
        }
      }
      const process = (selectedCity: string, selectedCityNames: string) => {

        if (foundCity.toLowerCase() !== selectedCity.toLowerCase() && selectedCityNames.toLowerCase().indexOf(foundCity.toLowerCase()) == -1) {
          console.error(`Selected address is not in ${selectedCity}`);
          this.form.get('address')?.reset();
          this.form.get('lat')?.reset();
          this.form.get('long')?.reset();
          alert(`Please select an address in ${selectedCity}`);
        }
      };

      if (this.citySelect.selectedCity === null) {
        const selectedCityID = this.form.get('city_id')?.value;
        if (selectedCityID) {
          this.loadCityById(selectedCityID).subscribe((city: City) => {
            const selectedCity = city.name;
            const selectedCityNames = city.alternatenames ?? "";
            process(selectedCity, selectedCityNames);
          });
        } else {
          alert('Please select a city first.');
        }
      } else {
        const selectedCity = this.citySelect.selectedCity.name;
        const selectedCityNames = this.citySelect.selectedCity.alternatenames ?? "";
        process(selectedCity, selectedCityNames);
      }
    } else {
      alert('Could not retrieve city information. Please select a valid address.');
    }
  }
  loadCityById(id: number): Observable<Model> {
    const query = new ApiQuery().addInclude('country');
    return this.cityService.show(id, query);
  }

  fillCoordinates() {
    this.form.get('lat').setValue(Number(this.lastGPCoordinates['lat']));
    this.form.get('long').setValue(Number(this.lastGPCoordinates['long']));
  }

  save() {
    if (this.venue && this.venue.id) {
      const venue: Venue = this.venue.deserialize(this.form.value);
      this.venueService.update(venue).subscribe((res) => {
        this.modal.close('Save click');
      });
    } else {
      const venue: Venue = new Venue().deserialize(this.form.value);
      this.venueService.store(venue).subscribe((res) => {
        this.modal.close(res);
      });
    }
  }

  selectLocation(event: any) {
    if (event && event.latLng) {
      const { lat, lng } = event.latLng.toJSON();
      this.form.get('lat')?.setValue(lat);
      this.form.get('long')?.setValue(lng);
      console.log('Location updated:', lat, lng);
    } else {
      console.warn('Event structure unexpected:', event);
    }
  }

  number(number: any): number {
    return Number(number);
  }

  /*
   * Генерира обекта с опциите за google places
   */
  generateGPOptions(): void {
    (this.form.get('city_id') as FormArray).valueChanges.subscribe((values) => {
      setTimeout(() => {
        // не работи без timeout не знам защо
        if (this.citySelect.selectedCity) {
          // сетва в обекта с опциите за google places да търси само в избраната държава
          this.GPOptions['componentRestrictions'] = {
            country: this.citySelect.selectedCity.country.code,
          };
          this.address.reset();
        }
      });
    });
  }

  private createForm() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      logo: [null],
      new_logo: [null],
      note: [''],
      address: ['', [Validators.required]],
      lat: [0, [Validators.required]],
      long: [0, [Validators.required]],
      city_id: [null, [Validators.required]],
      city: [null],
      founded_at: [null],
      rooms_count: [null],
      visitors_per_year: [null],
      area: [null],
      distance_to_center: [null],
      distance_to_airport: [null],
      distance_to_tr_point: [null],
      distance_to_tr_point_name: [null],
      capacity: [null],
      parking: [null],
      active: [true],
    });
  }

  private patchForm() {
    log.debug('patchForm');
    if (this.venue && this.venue.id) {
      this.venueService
        .show(this.venue.id, new ApiQuery().addIncludes('city.country', 'logo'))
        .subscribe((venue: Venue) => {
          venue.lat = Number(venue.lat);
          venue.long = Number(venue.long);
          this.form.patchValue(venue);

          // търси адрес само в избраната държава
          if (venue.city && venue.city.country) {
            this.GPOptions['componentRestrictions'] = {
              country: venue.city.country.code,
            };
            this.address.reset();
          }
        });
      this.fillParentFormCoordinates = false;
    } else if (this.venue) {
      // this.venue['city_id'] = this.venue.city.id;
      // this.venue['lat'] = Number(this.venue.lat);
      // this.venue['long'] = Number(this.venue.long);
      this.form.patchValue(this.venue);
      // this.fillParentFormCoordinates = false;
    }
  }
}
